import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Crown, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { track } from '../analytics/track';
import { useLocale } from '../i18n/LocaleContext';
import { isAppPath } from '../i18n/locales';
import { trackPurchaseOnce } from '../utils/analytics';
import { PACK_VALUE, takeCheckout } from '../utils/checkoutTrip';
import { markPendingVideoPaid, readPendingVideo } from '../utils/pendingVideo';
import { markPendingRemixPaid } from '../utils/pendingRemix';

const UpgradeOffer = lazy(() => import('../components/upgrade/UpgradeOffer'));
const FirstVideoOffer = lazy(() => import('../components/upgrade/FirstVideoOffer'));

// What stopped them, in the order asked (services/checkout_waiting.py ANSWERS)
export const ANSWERS = ['too_expensive', 'no_subscription', 'payment_problem', 'just_looking'];
// Asked once per visit: the line is not fetched again until the tab is reopened
const ASKED = 'cs_checkout_waiting_asked';

/**
 * Purchases the server credited with no page of the buyer's open (services/checkout_waiting.py
 * note_unreported: a tab closed on Stripe's page, an OXXO or boleto payment that settled later), each
 * reported to Google Ads once, under its checkout session id.
 */
export const reportLatePurchases = (notes) => {
  for (const note of Array.isArray(notes) ? notes : []) {
    if (!note?.session) continue;
    const currency = String(note.currency || 'USD').toUpperCase();
    const sent = trackPurchaseOnce({ value: Number(note.amount) || PACK_VALUE, currency, transactionId: note.session });
    track('purchase_reported_late', { kind: note.kind, value: Number(note.amount) || null, currency, sent });
  }
};

/**
 * Checkouts people leave, brought back (2026-10-18: 248 of 270 checkouts in a week were left with
 * Stripe's billing form untouched, and 8 of the 22 payers paid on a second checkout).
 *
 * - Back from a pack's checkout (`/ai-agent?pack=<session>`): the payment is confirmed (the API
 *   credits it once) and they are told the videos are there.
 * - Otherwise, on an app page: a checkout they opened and left (GET /subscription/waiting, from
 *   30 minutes on) is offered once, with "what stopped you?". Continue opens the same offer, or the
 *   pack's checkout; an answer or the close button hides it for good.
 * - On the first app page of every load: a pack, a Clip pack or a new subscription the server credited
 *   with no page of theirs open (the same GET's `unreported`) is reported to Google Ads, once.
 */
export default function CheckoutComeback() {
  const { isAuthenticated } = useAuth();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { t } = useLocale();
  const [waiting, setWaiting] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [added, setAdded] = useState(null);
  // Opened straight from the email: no card under the offer
  const [direct, setDirect] = useState(false);
  const confirming = useRef(false);
  const asked = useRef(false);
  const notesRead = useRef(false);

  // Back from a pack's checkout
  useEffect(() => {
    const sessionId = new URLSearchParams(search).get('pack');
    if (!isAuthenticated || !sessionId || confirming.current) return;
    confirming.current = true;
    const trip = takeCheckout();
    api.confirmPack(sessionId)
      .then(({ videos, paid }) => {
        setAdded(videos);
        track('pack_paid', { videos });
        // A first video paid for at the offer starts by itself on the AI Agent page (utils/pendingVideo.js)
        markPendingVideoPaid();
        // and a remix paid for at the Remix offer, wherever they are (components/Remix/PendingRemix.jsx)
        markPendingRemixPaid();
        // What Stripe took (the API's word), else what the checkout remembered
        const currency = paid?.currency || trip.currency;
        trackPurchaseOnce({ value: paid?.amount || trip.value || PACK_VALUE, transactionId: sessionId, ...(currency && { currency: currency.toUpperCase() }) });
      })
      .catch(() => track('pack_confirm_failed'))
      .finally(() => {
        const params = new URLSearchParams(search);
        params.delete('pack');
        navigate({ pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
      });
  }, [isAuthenticated, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // A checkout left, once a visit. From the reminder email's button (?resume=checkout) the reader
  // already said "continue", so the offer (or the pack's checkout) opens at once, with no card first.
  // The same answer carries the purchases to report late: those are read on the first app page of
  // every load (the signed-in home is one), also when the line was already asked in this tab
  useEffect(() => {
    if (!isAuthenticated || !(isAppPath(pathname) || pathname === '/')) return;
    const params = new URLSearchParams(search);
    // Back from a pack, a Clip pack or a subscription: that page confirms and reports its own purchase
    if (params.get('pack') || params.get('clip_pack') || pathname.startsWith('/subscription/success')) return;
    const fromEmail = params.get('resume') === 'checkout';
    let askNow = false;
    if (!asked.current && isAppPath(pathname)) {
      askNow = true;
      try {
        if (!fromEmail && sessionStorage.getItem(ASKED)) askNow = false;
        else sessionStorage.setItem(ASKED, '1');
      } catch {
        // Private windows: asked again on the next page instead, and the API still hides an answered one
      }
    }
    if (!askNow && notesRead.current) return;
    if (askNow) asked.current = true;
    notesRead.current = true;
    api.getWaitingCheckout()
      .then(({ waiting: found, unreported }) => {
        reportLatePurchases(unreported);
        if (!askNow || !found) return;
        track('checkout_waiting_shown', { kind: found.kind, reason: found.reason, from_email: fromEmail });
        if (fromEmail) {
          track('checkout_waiting_continue', { kind: found.kind, reason: found.reason, from_email: true });
          if (found.kind === 'pack') {
            api.createPackCheckout(found.reason).then(({ url }) => { window.location.href = url; }).catch(() => setWaiting(found));
            return;
          }
          setDirect(true);
          setWaiting(found);
          setOfferOpen(true);
          return;
        }
        setWaiting(found);
      })
      .catch(() => {});
  }, [isAuthenticated, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const answer = (choice) => {
    setAnswered(true);
    track('checkout_waiting_answer', { answer: choice, kind: waiting?.kind });
    api.answerWaitingCheckout(choice).catch(() => {});
    if (choice === 'dismissed') setWaiting(null);
    else setTimeout(() => setWaiting(null), 2500);
  };

  const resume = async () => {
    track('checkout_waiting_continue', { kind: waiting.kind, reason: waiting.reason });
    if (waiting.kind !== 'pack') {
      setOfferOpen(true);
      return;
    }
    setStarting(true);
    try {
      const { url } = await api.createPackCheckout(waiting.reason);
      window.location.href = url;
    } catch {
      setStarting(false);
    }
  };

  return (
    <>
      {added != null && (
        <div role="status" className="fixed inset-x-4 bottom-[calc(1rem+var(--consent-bar-h,0px))] z-50 mx-auto max-w-md rounded-xl border bg-card p-4 text-sm shadow-lg" data-testid="pack-added">
          <div className="flex items-start gap-3">
            <Crown className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-foreground">{t('checkoutWaiting.packAddedTitle', { n: added })}</p>
              <p className="mt-0.5 text-muted-foreground">{t('checkoutWaiting.packAddedText')}</p>
            </div>
            <button type="button" onClick={() => setAdded(null)} aria-label={t('checkoutWaiting.close')} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {waiting && added == null && !direct && (
        <div role="region" aria-label={t('checkoutWaiting.label')} className="fixed inset-x-4 bottom-[calc(1rem+var(--consent-bar-h,0px))] z-50 mx-auto max-w-md rounded-xl border bg-card p-4 text-sm shadow-lg" data-testid="checkout-waiting">
          <div className="flex items-start gap-3">
            <Crown className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-foreground">
                {t(waiting.kind === 'pack' ? 'checkoutWaiting.titlePack' : 'checkoutWaiting.titlePro')}
              </p>
              <p className="mt-0.5 text-muted-foreground">
                {t(waiting.kind === 'pack' ? 'checkoutWaiting.textPack' : 'checkoutWaiting.textPro', { price: waiting.label })}
              </p>
              {!answered && (
                <Button size="sm" className="mt-3" onClick={resume} disabled={starting} data-testid="checkout-waiting-continue">
                  {starting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                  {t('checkoutWaiting.continue')}
                </Button>
              )}
              <div className="mt-3 border-t pt-3">
                {answered ? (
                  <p className="text-muted-foreground">{t('checkoutWaiting.thanks')}</p>
                ) : (
                  <>
                    <p className="text-xs font-medium text-muted-foreground">{t('checkoutWaiting.question')}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {ANSWERS.map((choice) => (
                        <button key={choice} type="button" onClick={() => answer(choice)}
                          className="rounded-full border px-2.5 py-1 text-xs text-foreground hover:bg-muted" data-testid={`checkout-waiting-${choice}`}>
                          {t(`checkoutWaiting.answers.${choice}`)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            <button type="button" onClick={() => answer('dismissed')} aria-label={t('checkoutWaiting.close')} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {offerOpen && (
        <Suspense fallback={null}>
          {waiting?.reason === 'first_video' ? (
            // A new account's first video (since 2026-10-27): the same offer, with the idea kept in this browser
            <FirstVideoOffer open onClose={() => setOfferOpen(false)} idea={readPendingVideo()?.form?.initial_idea || ''} form={readPendingVideo()?.form || null} />
          ) : (
            <UpgradeOffer open onClose={() => setOfferOpen(false)} reason={waiting?.reason || 'videos'} source={direct ? 'checkout_reminder' : 'checkout_waiting'} />
          )}
        </Suspense>
      )}
    </>
  );
}
